// src/pages/reports/RewardsPointsBook/RewardsPointsBook.tsx
import React, { useState, useRef, useEffect } from "react";
import { Search } from "@mui/icons-material";
import CalendorIcon from "../../../assests/images/calendar-2.svg";
import sorterArrow from "../../../assests/images/SortingArrow.svg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Box,
  Link,
  Typography,
  TextField,
  InputAdornment,
  Button,
  ListItemText,
  ListItemButton,
  List,
  ButtonGroup,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import RewardsTable from "./RewardsTable";
import { debounce } from "lodash";
import RewardsTrTable from "./RewardsTrTable";
import RewardsFilterPopup from "./RewardsFilterPopup"; // summary popup
import RewardsTrFilterPopup from "./RewardsTrFilterPopup"; // transactions popup
import arrowLeft from "../../../assests/images/arrow-left.svg";
import arrowRight from "../../../assests/images/arrow-right.png";
import doubleArrowLeft from "../../../assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../assests/images/double-arrow-right.svg";

const CustomInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick, onClear }, ref) => (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #ccc",
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "0 10px",
        height: "48px",
        width: "301px",
        cursor: "pointer",
      }}
    >
      <input
        ref={ref}
        value={value || "Select Date Range"}
        style={{
          fontSize: "14px",
          border: "none",
          outline: "none",
          flex: 1,
          color: "#000",
          backgroundColor: "transparent",
        }}
        readOnly
        onClick={onClick}
      />
      {value && (
        <img
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          src={
            "data:image/svg+xml;utf8," +
            encodeURIComponent(
              `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
            )
          }
          alt="Clear"
          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 8 }}
        />
      )}
      <img
        src={CalendorIcon}
        alt="Calendar Icon"
        style={{ height: "20px", width: "20px", cursor: "pointer" }}
        onClick={onClick}
      />
    </div>
  )
);

// shape for filters passed to child components (keeps your original nulls)
interface AppliedFilters {
  locations?: string[] | null;
  outletTypes?: string[] | null;
  outletNames?: string[] | null;
  lastloginStart?: string | null;
  lastloginEnd?: string | null;
  pmi_entity_code?: string[] | null;

  // ⭐ NEW retailer-level fields
  region?: string[] | null;
  asm_name?: string[] | null;
  area_code?: string[] | null;
  tse_name?: string[] | null;
  territory_code?: string[] | null;
  outlet_salesforce_id?: string[] | null;
  sfa_id?: string[] | null;

  [k: string]: any;
}

// timezone-safe YYYY-MM-DD (matches Reports flow)
const toYmd = (d: Date | null) =>
  d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : null;

function RewardsPointsBook() {
  // 🆕 separate ranges for each tab
  const [dateRangeSummary, setDateRangeSummary] = useState<[Date | null, Date | null]>([null, null]);
  const [dateRangeTrx, setDateRangeTrx] = useState<[Date | null, Date | null]>([null, null]);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState<number>(0);
  const sortButtonRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const [filterKeySummary, setFilterKeySummary] = useState(0);
  const [filterKeyTrx, setFilterKeyTrx] = useState(0);
  const [sortOption, setSortOption] = useState<"highest" | "lowest" | null>(null);
  const [isSwitch, setIsSwitch] = useState(false); // false -> Summary, true -> Transactions
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  // store backend filterOptions returned from API (pass to popup)
  const [backendFilterOptions, setBackendFilterOptions] = useState<any | null>(null);

  // separate filter modal state for each toggle
  const [isFilterOpenSummary, setIsFilterOpenSummary] = useState(false);
  const [isFilterOpenTrx, setIsFilterOpenTrx] = useState(false);

  const SEARCH_PLACEHOLDERS = {
    summary: "Search by User Id ,User Name, Outlet Name, Location",
    transactions: "Search by First Name, Last Name ,Outlet Name, City,Phone Number",
  };
  const searchPlaceholder = isSwitch ? SEARCH_PLACEHOLDERS.transactions : SEARCH_PLACEHOLDERS.summary;

  // keep separate applied filters
  const [appliedFiltersSummary, setAppliedFiltersSummary] = useState<AppliedFilters | null>(null);
  const [appliedFiltersTrx, setAppliedFiltersTrx] = useState<AppliedFilters | null>(null);

  // control when popups should fetch initial filter options
  const [shouldFetchFilters, setShouldFetchFilters] = useState(false);

  // 🔁 active header range based on tab
  const activeRange = isSwitch ? dateRangeTrx : dateRangeSummary;
  const [lastloginStart, lastloginEnd] = activeRange;

  // debounce setup
  const debouncerRef = useRef<any>(null);
  useEffect(() => {
    debouncerRef.current = debounce((q: string) => {
      setDebouncedSearch(q);
    }, 300);
    return () => {
      debouncerRef.current.cancel();
    };
  }, []);

  useEffect(() => {
    if (debouncerRef.current) debouncerRef.current(searchQuery);
  }, [searchQuery]);

  const handleSortButtonClick = () => setIsPopupOpen((s) => !s);

  // Set popup position dynamically when it opens
  useEffect(() => {
    if (sortButtonRef.current && isPopupOpen) {
      const buttonRect = sortButtonRef.current.getBoundingClientRect();
      setPopupPosition(buttonRect.bottom);
    }
  }, [isPopupOpen]);

  // Close sort popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node) && !sortButtonRef.current?.contains(event.target as Node)) {
        setIsPopupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close filter modals on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsFilterOpenSummary(false);
        setIsFilterOpenTrx(false);
        setIsPopupOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSortSelection = (option: "highest" | "lowest") => {
    setSortOption(option);
    setIsPopupOpen(false);
  };

  // 🆕 update only the active tab’s header date range
  const handleDateChange = (update: [Date | null, Date | null]) => {
    if (isSwitch) {
      setDateRangeTrx(update);
    } else {
      setDateRangeSummary(update);
    }
  };

  // convert any nulls to undefined for initialFilters
  const normalizeToInitialFilters = (f: AppliedFilters | null): any | null => {
    if (!f) return null;
    return {
      locations: f.locations ?? undefined,
      outletTypes: f.outletTypes ?? undefined,
      outletNames: f.outletNames ?? undefined,
      pmi_entity_code: f.pmi_entity_code ?? undefined,
      transactionTypes: f.transactionTypes ?? undefined,
      redemptionStatuses: f.redemptionStatuses ?? undefined,
      lastloginStart: f.lastloginStart ?? undefined,
      lastloginEnd: f.lastloginEnd ?? undefined,
      memberSinceStart: f.memberSinceStart ?? undefined,
      memberSinceEnd: f.memberSinceEnd ?? undefined,
      backendFilters: f.backendFilters ?? undefined,

      // ⭐ NEW (top-level copies are harmless; popup rehydrates from backendFilters)
      region: f.region ?? undefined,
      asm_name: f.asm_name ?? undefined,
      area_code: f.area_code ?? undefined,
      tse_name: f.tse_name ?? undefined,
      territory_code: f.territory_code ?? undefined,
      outlet_salesforce_id: f.outlet_salesforce_id ?? undefined,
      sfa_id: f.sfa_id ?? undefined,
    };
  };

  // Handlers for Apply
  const handleFilterApplySummary = (payload: any) => {
    const activeCount = countActiveFilters(payload);

    if (activeCount === 0) {
      setAppliedFiltersSummary(null);
    } else {
      setAppliedFiltersSummary({
        locations: payload.locations ?? null,
        outletTypes: payload.outletTypes ?? null,
        outletNames: payload.outletNames ?? null,
        pmi_entity_code: payload.pmi_entity_code ?? null,
        lastloginStart: payload.lastloginStart ?? null,
        lastloginEnd: payload.lastloginEnd ?? null,
        memberSinceStart: payload.memberSinceStart ?? null,
        memberSinceEnd: payload.memberSinceEnd ?? null,
        backendFilters: payload.backendFilters ?? undefined,

        // ⭐ NEW retailer-level fields
        region: payload.region ?? null,
        asm_name: payload.asm_name ?? null,
        area_code: payload.area_code ?? null,
        tse_name: payload.tse_name ?? null,
        territory_code: payload.territory_code ?? null,
        outlet_salesforce_id: payload.outlet_salesforce_id ?? null,
        sfa_id: payload.sfa_id ?? null,
      });
    }

    setFilterKeySummary((k) => k + 1);
    setIsFilterOpenSummary(false);
  };

  const handleFilterApplyTrx = (payload: any) => {
    const activeCount = countActiveFilters(payload);

    if (activeCount === 0) {
      setAppliedFiltersTrx(null);
    } else {
      setAppliedFiltersTrx({
        locations: payload.locations ?? null,
        outletTypes: payload.outletTypes ?? null,
        outletNames: payload.outletNames ?? null,
        transactionTypes: payload.transactionTypes ?? null,
        redemptionStatuses: payload.redemptionStatuses ?? null,
        pmi_entity_code: payload.pmi_entity_code ?? null,
        lastloginStart: payload.lastloginStart ?? null,
        lastloginEnd: payload.lastloginEnd ?? null,
        memberSinceStart: payload.memberSinceStart ?? null,
        memberSinceEnd: payload.memberSinceEnd ?? null,
        backendFilters: payload.backendFilters ?? undefined,

        // ⭐ NEW retailer-level fields (now persisted for Transactions too)
        region: payload.region ?? null,
        asm_name: payload.asm_name ?? null,
        area_code: payload.area_code ?? null,
        tse_name: payload.tse_name ?? null,
        territory_code: payload.territory_code ?? null,
        outlet_salesforce_id: payload.outlet_salesforce_id ?? null,
        sfa_id: payload.sfa_id ?? null,
      });
    }

    // ✅ re-run trx table
    setFilterKeyTrx((k) => k + 1);
    setIsFilterOpenTrx(false);
  };

  // open the correct filter modal
  const openFilterForCurrentView = () => {
    setBackendFilterOptions(null);
    setShouldFetchFilters(true);

    if (isSwitch) {
      setIsFilterOpenTrx(true);
    } else {
      setIsFilterOpenSummary(true);
    }
  };

  // counts non-empty fields
  const countActiveFilters = (f: any | null) => {
    if (!f) return 0;
    let count = 0;
    const arrFields = [
      "locations",
      "outletTypes",
      "outletNames",
      "pmi_entity_code",
      "pmiEntityCodes",
      "transactionTypes",
      "redemptionStatuses",

      // ⭐ NEW
      "region",
      "asm_name",
      "area_code",
      "tse_name",
      "territory_code",
      "outlet_salesforce_id",
      "sfa_id",
    ];
    arrFields.forEach((k) => {
      if (f[k] && Array.isArray(f[k]) && f[k].length > 0) count += 1;
    });
    if (f.lastloginStart || f.lastloginEnd) count += 1;
    if (f.memberSinceStart || f.memberSinceEnd) count += 1;
    if (f.backendFilters && typeof f.backendFilters === "object") {
      const anySet = Object.values(f.backendFilters).some(
        (v) => v !== null && v !== undefined && (!(Array.isArray(v)) || v.length > 0)
      );
      if (anySet) count += 1;
    }
    return count;
  };

  // 🆕 keep each tab’s applied filters in sync with its own header range
  useEffect(() => {
    const [s, e] = dateRangeSummary;
    const startStr = toYmd(s);
    const endStr = toYmd(e);
    setAppliedFiltersSummary((prev) => {
      const base = prev ?? {};
      return { ...base, lastloginStart: startStr, lastloginEnd: endStr };
    });
  }, [dateRangeSummary]);

  useEffect(() => {
    const [s, e] = dateRangeTrx;
    const startStr = toYmd(s);
    const endStr = toYmd(e);
    setAppliedFiltersTrx((prev) => {
      const base = prev ?? {};
      return { ...base, lastloginStart: startStr, lastloginEnd: endStr };
    });
  }, [dateRangeTrx]);

  // overlay style (used by both modals)
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.35)",
    zIndex: 2000,
    padding: 16,
  };

  return (
    <Box sx={{ background: "#fff", paddingTop: "30px" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 32px",
        }}
      >
        <Box>
          <Link
            href="#"
            sx={{
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: "500",
              color: "#696A6B",
              paddingRight: "10px",
            }}
          >
            Rewards Book
          </Link>
          <Typography
            variant="h3"
            sx={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#000",
              marginTop: "16px",
            }}
          >
            Rewards & Points Book
          </Typography>
        </Box>

        {/* Right controls: Filter button + Switch buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Filter Button */}
          <Button
            onClick={openFilterForCurrentView}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              bgcolor: "#C8172F",
              color: "#fff",
              borderColor: "#C8172F",
              borderRadius: "8px",
              height: "40px",
              width: "74px",
              "&:hover": {
                bgcolor: "#a31425",
                borderColor: "#a31425",
              },
            }}
          >
            Filter
          </Button>

          <ButtonGroup variant="outlined" sx={{ mr: 0 }}>
            <Button
              onClick={() => setIsSwitch(false)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                bgcolor: !isSwitch ? "#C8172F" : "transparent",
                color: !isSwitch ? "#fff" : "#000",
                borderColor: "#C8172F",
                borderRadius: "8px",
                height: "40px",
                width: "110px",
                "&:hover": {
                  bgcolor: !isSwitch ? "#a31425" : "rgba(200,23,47,0.1)",
                  borderColor: "#a31425",
                },
              }}
            >
              Summary
            </Button>

            <Button
              onClick={() => setIsSwitch(true)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                bgcolor: isSwitch ? "#C8172F" : "transparent",
                color: isSwitch ? "#fff" : "#000",
                borderColor: "#C8172F",
                borderRadius: "8px",
                height: "40px",
                width: "110px",
                "&:hover": {
                  bgcolor: isSwitch ? "#a31425" : "rgba(200,23,47,0.1)",
                  borderColor: "#a31425",
                },
              }}
            >
              Transactions
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {/* Search + Filters */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "16px",
          padding: "0 32px 30px",
        }}
      >
        {/* Search */}
        <Box sx={{ maxWidth: "520px", flexGrow: 1 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            type="search"
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ height: "20px", width: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": { border: "2px solid #F5F5F5" },
                height: "48px",
              },
            }}
          />
        </Box>

        {/* Date Picker (header) */}
        <Box sx={{ display: "flex" }}>
          <Box>
            <DatePicker
              selectsRange
              startDate={activeRange[0] || undefined}
              endDate={activeRange[1] || undefined}
              onChange={handleDateChange}
              dateFormat="yyyy-MM-dd"
              customInput={
                <CustomInput
                  onClear={() => {
                    if (isSwitch) {
                      setDateRangeTrx([null, null]);
                      setAppliedFiltersTrx((prev) => (prev ? { ...prev, lastloginStart: null, lastloginEnd: null } : prev));
                    } else {
                      setDateRangeSummary([null, null]);
                      setAppliedFiltersSummary((prev) => (prev ? { ...prev, lastloginStart: null, lastloginEnd: null } : prev));
                    }
                  }}
                />
              }
              renderCustomHeader={({ date, decreaseMonth, increaseMonth, decreaseYear, increaseYear }) => (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <button onClick={decreaseYear} aria-label="Previous year">
                    <img src={doubleArrowLeft} alt="Previous year" />
                  </button>
                  <button onClick={decreaseMonth} aria-label="Previous month">
                    <img src={arrowLeft} alt="Previous month" />
                  </button>

                  <span style={{ margin: "0 10px" }}>
                    {date.toLocaleString("default", { year: "numeric", month: "long" })}
                  </span>

                  <button onClick={increaseMonth} aria-label="Next month">
                    <img src={arrowRight} alt="Next month" />
                  </button>
                  <button onClick={increaseYear} aria-label="Next year">
                    <img src={doubleArrowRight} alt="Next year" />
                  </button>
                </div>
              )}
            />
          </Box>
        </Box>
      </Box>

      {/* Sort Popup */}
      {isPopupOpen && (
        <Box
          ref={popupRef}
          sx={{
            position: "absolute",
            top: `${popupPosition + 10}px`,
            right: "30px",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "10px 0",
            zIndex: 999,
            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            width: "320px",
          }}
        >
          <List>
            {[
              { label: "Highest to Lowest Points", value: "highest" },
              { label: "Lowest to Highest Points", value: "lowest" },
            ].map(({ label, value }) => (
              <ListItemButton key={value} onClick={() => setSortOption(value as "highest" | "lowest")}>
                <ListItemText primary={label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      )}

      {/* Table (pass appliedFilters + correct dates) */}
      <Box
        sx={{
          width: "100%",
          background: "#f9fbfc",
          padding: "20px 30px",
          borderTop: "1px solid #d3d3d3",
        }}
      >
        {isSwitch ? (
          <RewardsTrTable
            startDate={dateRangeTrx[0]}
            endDate={dateRangeTrx[1]}
            searchQuery={debouncedSearch}
            sortOption={sortOption}
            selectedColumns={selectedColumns}
            appliedFilters={appliedFiltersTrx}
            onBackendFilterOptions={setBackendFilterOptions}
            filterKey={filterKeyTrx}
          />
        ) : (
          <RewardsTable
            startDate={dateRangeSummary[0]}
            endDate={dateRangeSummary[1]}
            searchQuery={debouncedSearch}
            sortOption={sortOption}
            selectedColumns={selectedColumns}
            appliedFilters={appliedFiltersSummary}
          />
        )}
      </Box>

      {/* Summary filter modal */}
      {isFilterOpenSummary && (
        <div
          style={overlayStyle}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsFilterOpenSummary(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", width: "auto" }}>
            <RewardsFilterPopup
              isOpen={isFilterOpenSummary}
              shouldFetch={shouldFetchFilters}
              onFetched={() => setShouldFetchFilters(false)}
              onApplyFilters={(payload: any) => handleFilterApplySummary(payload)}
              onCancel={() => setIsFilterOpenSummary(false)}
              // 🆕 pass Summary range + setter
              dateRange={[dateRangeSummary[0], dateRangeSummary[1]]}
              setDateRange={(r: any) => setDateRangeSummary(r)}
              initialFilters={normalizeToInitialFilters(appliedFiltersSummary)}
            />
          </div>
        </div>
      )}

      {/* Transactions filter modal */}
      {isFilterOpenTrx && (
        <div
          style={overlayStyle}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsFilterOpenTrx(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", width: "auto" }}>
            <RewardsTrFilterPopup
              isOpen={isFilterOpenTrx}
              shouldFetch={shouldFetchFilters}
              onFetched={() => setShouldFetchFilters(false)}
              onApplyFilters={(payload: any) => handleFilterApplyTrx(payload)}
              // @ts-ignore
              onCancel={() => setIsFilterOpenTrx(false)}
              // 🆕 pass Transactions range + setter
              dateRange={[dateRangeTrx[0], dateRangeTrx[1]]}
              setDateRange={(r: any) => setDateRangeTrx(r)}
              initialFilters={normalizeToInitialFilters(appliedFiltersTrx)}
              backendFilterOptions={backendFilterOptions}
            />
          </div>
        </div>
      )}
      <Box
        sx={{
          mt: 6,
          px: 4,
          py: 2,
          borderTop: "1px solid #eee",
          backgroundColor: "#fff",
        }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBackIosNewIcon fontSize="small" />}
          onClick={() => window.history.back()}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#C8172F",
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          Back
        </Button>
      </Box>
    </Box>
  );
}

export default RewardsPointsBook;
